import { useState } from "react";
import PortalGlassLightbox from "../PortalGlassLightbox";
import { constructInputWithSpecialExpressionList } from "../../../utilization/CatchtivityUtilization";
import {
  getImageDimensions,
  rememberImageDimensions,
} from "../../../utilization/ImageDimensionCache";
import { InlineMath } from "react-katex";
import Icon from "../../icons/Icon";
import i18n from "../../../language/i18n";
import AudioPlayer from "../../../../components/learner/audio/AudioPlayer";
import {
  IInputPart,
  IShowBodyMediaByContentTypeProps,
} from "../../../properties/ActivityProperties";

/** The player reports progress to its host; as a question stem there is no host to tell. */
const NOOP = () => {};

const ShowBodyMediaByContentType = ({
  index,
  type,
  value,
  size,
  floatMode,
  canFloat,
}: IShowBodyMediaByContentTypeProps) => {
  const [showFullScreen, setShowFullScreen] = useState<boolean>(false);
  const [selectedFullScreenItem, setSelectedFullScreenItem] =
    useState<string>("");
  // Natural size of the current IMAGE. Read cache-first (the solver prefetch warms
  // it) so a preloaded image reserves its exact box on FIRST paint — no
  // collapse-then-reflow. The load-time fallback is tagged with its own url so a
  // reused component instance never carries the PREVIOUS activity's dimensions.
  const [loadedImgSize, setLoadedImgSize] = useState<{
    url: string;
    width: number;
    height: number;
  } | null>(null);
  const imgSize =
    getImageDimensions(value) ||
    (loadedImgSize && loadedImgSize.url === value ? loadedImgSize : null);

  const convertToPercentage = (size: string) => {
    switch (size) {
      case "1/3":
        return "med-w-small";
      case "1/2":
        return "med-w-medium";
      case "1":
        return "med-w-large";
      default:
        return "";
    }
  };

  // Orientation derived from the natural size (cache-first, so it's known WITHOUT
  // waiting for onLoad when preloaded) — a passage image floats correctly on first
  // paint instead of flipping full-width → float once the pixels arrive.
  const effectiveOrient = imgSize
    ? imgSize.width / imgSize.height < 1.15
      ? "portrait"
      : "landscape"
    : null;

  // Figure-float modifier for the IMAGE wrapper (passage / open-ended bodies).
  // Passage floats only a PORTRAIT image (prose wraps beside it) when there's
  // following text and the container is wide enough (gated by @container in
  // mediaStyles); open-ended floats ANY image. Everything else — and every
  // other template — is a full-width block.
  const passageImgClass = floatMode
    ? canFloat && (floatMode === "any" || effectiveOrient === "portrait")
      ? "med-pimg-float"
      : "med-pimg-full"
    : "";

  const renderSpecialExpressions = (
    inputPart: IInputPart,
    inputPartIndex: number
  ) => {
    return (
      <span
        key={inputPartIndex}
        className={`${inputPart.isBold ? "mc-bold" : ""} ${
          inputPart.isUnderline ? "mc-underline" : ""
        }`}
      >
        {inputPart.isEquation ? (
          <span className="mc-xl">
            <InlineMath math={inputPart.value} />
          </span>
        ) : (
          inputPart.value
        )}
      </span>
    );
  };

  const renderTextContent = (text: string, itemKey: string) => {
    return (
      <span key={itemKey} className="mc-xl">
        {constructInputWithSpecialExpressionList(text).map(
          (inputPart: IInputPart, inputPartIndex: number) =>
            renderSpecialExpressions(inputPart, inputPartIndex)
        )}
      </span>
    );
  };

  // Create a fullscreen image component
  const handleOpenFullScreen = (imageSource: string) => {
    setSelectedFullScreenItem(imageSource);
    setShowFullScreen(true);
  };

  const retrieveValueParts = (value: string) => {
    let currentIndex = 0;
    const valuePartList = [];
    let copyValue = JSON.parse(JSON.stringify(value));

    const processTextTags = () => {
      const checkText = "--TEXT--";
      while (copyValue.includes(checkText)) {
        const firstIndex = copyValue.indexOf(checkText);
        const textBeforeTag = copyValue.substring(0, firstIndex);

        if (textBeforeTag.trim() !== "") {
          currentIndex++;
          const itemKey = `text-before-${index}-${currentIndex}`;
          valuePartList.push(renderTextContent(textBeforeTag, itemKey));
        }

        const subValue = copyValue.substring(firstIndex + checkText.length);
        const secondIndex = subValue.indexOf(checkText);
        if (secondIndex === -1) break;

        const textInsideTag = copyValue.substring(
          firstIndex + checkText.length,
          firstIndex + checkText.length + secondIndex
        );
        currentIndex++;
        const itemKey = `text-inside-${index}-${currentIndex}`;

        valuePartList.push(
          <span key={itemKey} className="med-xl-bold">
            {constructInputWithSpecialExpressionList(textInsideTag).map(
              (inputPart: IInputPart, inputPartIndex: number) =>
                renderSpecialExpressions(inputPart, inputPartIndex)
            )}
          </span>
        );

        copyValue = copyValue.substring(
          firstIndex + checkText.length + secondIndex + checkText.length
        );
      }
    };

    const processImageTags = () => {
      const checkText = "--IMAGE--";
      while (copyValue.includes(checkText)) {
        const firstIndex = copyValue.indexOf(checkText);
        const textBeforeTag = copyValue.substring(0, firstIndex);

        if (textBeforeTag.trim() !== "") {
          currentIndex++;
          valuePartList.push(
            <p
              key={`text-before-img-${index}-${currentIndex}`}
              className="mc-xl"
            >
              {textBeforeTag}
            </p>
          );
        }

        const subValue = copyValue.substring(firstIndex + checkText.length);
        const secondIndex = subValue.indexOf(checkText);
        if (secondIndex === -1) break;

        const imageSource = copyValue.substring(
          firstIndex + checkText.length,
          firstIndex + checkText.length + secondIndex
        );

        currentIndex++;
        valuePartList.push(
          <div
            key={`img-${index}-${currentIndex}`}
            className="med-img200"
          >
            {imageSource ? (
              <img
                src={imageSource}
                alt="media"
                className="med-rounded"
                style={{ objectFit: "contain" }}
              />
            ) : null}
            <div
              className="med-fs-btn"
              onClick={() => handleOpenFullScreen(imageSource)}
            >
              <Icon name="arrow-up" size={16} />
            </div>
          </div>
        );

        copyValue = copyValue.substring(
          firstIndex + checkText.length + secondIndex + checkText.length
        );
      }
    };

    const processVideoTags = () => {
      const checkText = "--VIDEO--";
      while (copyValue.includes(checkText)) {
        const firstIndex = copyValue.indexOf(checkText);
        const textBeforeTag = copyValue.substring(0, firstIndex);

        if (textBeforeTag.trim() !== "") {
          currentIndex++;
          valuePartList.push(
            <p
              key={`text-before-video-${index}-${currentIndex}`}
              className="mc-xl"
            >
              {textBeforeTag}
            </p>
          );
        }

        const subValue = copyValue.substring(firstIndex + checkText.length);
        const secondIndex = subValue.indexOf(checkText);
        if (secondIndex === -1) break;

        const videoSource = copyValue.substring(
          firstIndex + checkText.length,
          firstIndex + checkText.length + secondIndex
        );

        currentIndex++;
        valuePartList.push(
          <video
            key={`video-${index}-${currentIndex}`}
            src={videoSource}
            className="med-v200"
            controls
          />
        );

        copyValue = copyValue.substring(
          firstIndex + checkText.length + secondIndex + checkText.length
        );
      }
    };

    const processAudioTags = () => {
      const checkText = "--AUDIO--";
      while (copyValue.includes(checkText)) {
        const firstIndex = copyValue.indexOf(checkText);
        const textBeforeTag = copyValue.substring(0, firstIndex);

        if (textBeforeTag.trim() !== "") {
          currentIndex++;
          valuePartList.push(
            <p
              key={`text-before-audio-${index}-${currentIndex}`}
              className="mc-xl"
            >
              {textBeforeTag}
            </p>
          );
        }

        const subValue = copyValue.substring(firstIndex + checkText.length);
        const secondIndex = subValue.indexOf(checkText);
        if (secondIndex === -1) break;

        const audioSource = copyValue.substring(
          firstIndex + checkText.length,
          firstIndex + checkText.length + secondIndex
        );

        currentIndex++;
        valuePartList.push(
          <audio
            key={`audio-${index}-${currentIndex}`}
            src={audioSource}
            className="med-v200"
            controls
          />
        );

        copyValue = copyValue.substring(
          firstIndex + checkText.length + secondIndex + checkText.length
        );
      }
    };

    processTextTags();
    processImageTags();
    processVideoTags();
    processAudioTags();

    if (copyValue.trim() !== "") {
      currentIndex++;

      const regexMatchImageText = copyValue.match(/<image>([\s\S]*?)<\/image>/);
      if (regexMatchImageText) {
        const imageText = regexMatchImageText[1];
        valuePartList.push(
          <div
            key={`img-desc-${index}-${currentIndex}`}
            className="med-imgdesc"
          >
            <div className="med-imgdesc-label">
              <p className="mc-bold">{i18n.t("image_description")}</p>
            </div>
            <span className="mc-xl">{imageText}</span>
          </div>
        );
      } else {
        const itemKey = `remaining-${index}-${currentIndex}`;
        valuePartList.push(renderTextContent(copyValue, itemKey));
      }
    }

    return valuePartList;
  };

  // The enlarged image opens in the shared zoomable lightbox (wheel / pinch / drag / double-click,
  // −/+/reset, Esc and backdrop close) — the same viewer the MCSA options use.
  const RenderShowFullScreenItem = () => {
    if (!showFullScreen || !selectedFullScreenItem) return null;
    return (
      <PortalGlassLightbox
        src={selectedFullScreenItem}
        onClose={() => {
          setShowFullScreen(false);
          setSelectedFullScreenItem("");
        }}
      />
    );
  };

  const RenderMainContent = () => {
    switch (type) {
      case "TEXT":
        return (
          <div className="med-text-wrap">
            <p className="med-pre">{retrieveValueParts(value)}</p>
          </div>
        );
      case "IMAGE":
        return (
          <div className={`med-img-wrap ${passageImgClass}`.trim()}>
            <div className="med-rel">
              {value ? (
                <img
                  // Key on the URL so a new activity mounts a FRESH <img> instead of
                  // reusing the node and painting the previous activity's picture
                  // until the new src loads.
                  key={value}
                  src={value}
                  alt="body-image"
                  className="med-img-max med-img-inner"
                  // width/height give the browser the aspect ratio up front so it
                  // reserves the correct box before the pixels arrive (no reflow).
                  width={imgSize?.width}
                  height={imgSize?.height}
                  style={{ objectFit: "contain" }}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth && img.naturalHeight) {
                      rememberImageDimensions(
                        value,
                        img.naturalWidth,
                        img.naturalHeight,
                      );
                      setLoadedImgSize({
                        url: value,
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                      });
                    }
                  }}
                />
              ) : null}
              <div
                className="med-fs-btn"
                onClick={() => handleOpenFullScreen(value)}
              >
                <Icon name="arrow-up" size={16} />
              </div>
            </div>
          </div>
        );
      case "VIDEO":
        return (
          <div className="med-av-wrap">
            <video
              src={value}
              className={`${convertToPercentage(size)} med-rounded`}
              controls
            />
          </div>
        );
      case "AUDIO":
        // The learner AudioPlayer, the same one the OPTIONS and an assignment material use:
        // play/pause, a scrubber with real elapsed time, a playback-rate control. A listening
        // stem is replayed several times and scrubbed back to a phrase, which a bare
        // <audio controls> makes awkward - and it left the stem looking unlike every other
        // recording in the same question.
        return (
          <div className="med-av-wrap">
            <AudioPlayer
              // Keyed on the source: the player owns its play/rate/elapsed state, and
              // swapping src underneath it left the previous clip's controls on screen.
              key={value}
              audioFileList={[{ url: value }]}
              currentAudioIndex={0}
              onAudioIndexChange={NOOP}
              onTimeUpdate={NOOP}
              onPlayStateChange={NOOP}
              isExternallyPaused={false}
              seekToMs={null}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div key={`body-content-${index}`} className="med-root">
      {RenderShowFullScreenItem()}
      {RenderMainContent()}
    </div>
  );
};

export default ShowBodyMediaByContentType;
